// Basic
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import PageActions from '@/common-utils/components/renders/pageActions/PageActions';
import { Badge } from '@/common-utils/components/ui/badge';
import { Skeleton } from '@/common-utils/components/ui/skeleton';
// Common
import ICONS from '@/common-utils/enums/icons';
import CUSTOMIZATION_STATUS from '../../enums/CustomizationStatus';
import RouteUrls from '@/app/root/enums/RouteUrls';
import CUSTOMIZATION_URL_ACTIONS from '../../enums/CustomizationUrlActions';
import MERCHANT_ACCOUNT_DETAILS_TABS from '@/app/root/enums/tabs/MerchantAccountDetailsTabs';
import { buildUrl, replaceParameters } from '@/common-utils/functions/restUtils';
// Lang
import { nsCustomization } from '@/app/i18n/namespaces';
import actionsCustomization from '../../api/actionsCustomization';
import { useEffect, useState } from 'react';
import { useToast } from '@/common-utils/hooks/use-toast';
import TooltipHelper from '@/common-utils/components/renders/tooltip/TooltipHelper';
import Icon from '@/common-utils/components/renders/icon/Icon';

/**
 *
 * @param root0
 * @param root0.customization
 * @param root0.isLoading
 * @param root0.action
 * @param root0.merchantAccountId
 * @param root0.isDisabled
 * @param root0.setIsLoading
 * @param root0.isEverythingValid
 * @param root0.customizationPackage
 * @param root0.refetchCustomization
 * @param root0.setCustomizationUuid
 * @param root0.isDirty
 * @param root0.setIsDirty
 * @returns {JSX.Element}
 */
function PageActionsHeader({
    customization,
    setIsLoading,
    action,
    isLoading = undefined,
    merchantAccountId,
    isEverythingValid,
    customizationPackage,
    refetchCustomization,
    setCustomizationUuid,
    isDirty,
    setIsDirty,
    setBeneficiaries
}) {

    // Toast
    const { toast } = useToast();

    // Router
    const navigate = useNavigate();

    // State
    const [renderedBadge, setRenderedBadge] = useState();
    const [canBeSaved, setCanBeSaved] = useState(false);
    const [canBePublished, setCanBePublished] = useState(false);

    // API Calls
    const onSuccessSaveCustomization = (data) => {
        // This triggers on creation or duplication success
        if (!action || action === CUSTOMIZATION_URL_ACTIONS.DUPLICATE) {
            setCustomizationUuid(data.data.uuid);
            navigate(
                buildUrl(
                    replaceParameters(RouteUrls.PAYLUDD_CUSTOMIZATION_MANAGEMENT.url, {
                        contractAccessId: merchantAccountId,
                        customizationId: data.data.uuid
                    }),
                    {
                        view: MERCHANT_ACCOUNT_DETAILS_TABS.CUSTOMIZATION.name,
                        action: CUSTOMIZATION_URL_ACTIONS.EDIT
                    }
                )
            );
        }
        setIsDirty(false);
        toast({
            description:
                <Entity
                    entity={'actions.page.save.success'}
                    namespace={nsCustomization}
                />,
            variant: 'success'
        });
    };
    const onErrorSaveCustomization = () => {
        toast({
            description:
                <Entity
                    entity={'actions.page.save.error'}
                    namespace={nsCustomization}
                />,
            variant: 'danger'
        });
    };
    const submitSaveCustomization = actionsCustomization.saveCustomization(onSuccessSaveCustomization, onErrorSaveCustomization);

    const onSuccessUpdateCustomization = () => {
        refetchCustomization();
        setIsDirty(false);
        toast({
            description:
                <Entity
                    entity={'actions.page.save.success'}
                    namespace={nsCustomization}
                />,
            variant: 'success'
        });
    };
    const onErrorUpdateCustomization = () => {
        toast({
            description:
                <Entity
                    entity={'actions.page.save.error'}
                    namespace={nsCustomization}
                />,
            variant: 'danger'
        });
    };
    const submitUpdateCustomization = actionsCustomization.updateCustomization(onSuccessUpdateCustomization, onErrorUpdateCustomization);

    const onSuccessPublishCustomization = () => {
        navigate(buildUrl(
            replaceParameters(
                RouteUrls.PAYLUDD_CUSTOMIZATION_MANAGEMENT.url, { contractAccessId: merchantAccountId, customizationId: customizationPackage.uuid }
            ),
            {
                view: MERCHANT_ACCOUNT_DETAILS_TABS.CUSTOMIZATION.name,
                action: CUSTOMIZATION_URL_ACTIONS.SHOW
            }
        ));
        refetchCustomization();
        setIsDirty(false);
        toast({
            description:
                <Entity
                    entity={'actions.page.publish.success'}
                    namespace={nsCustomization}
                />,
            variant: 'success'
        });
    };
    const onErrorPublishCustomization = () => {
        toast({
            description:
                <Entity
                    entity={'actions.page.publish.error'}
                    namespace={nsCustomization}
                />,
            variant: 'danger'
        });
    };
    const submitPublishCustomization = actionsCustomization.publishCustomization(onSuccessPublishCustomization, onErrorPublishCustomization);

    // Hooks
    useEffect(() => {
        setIsLoading(submitSaveCustomization.isLoading || submitPublishCustomization.isLoading || submitUpdateCustomization.isLoading);
    }, [submitSaveCustomization.isLoading, submitPublishCustomization.isLoading, submitUpdateCustomization.isLoading]);

    useEffect(() => {
        if (isEverythingValid && !isDirty) {
            // Allow to publish the customization
            setCanBePublished(true);
            setCanBeSaved(false);
        }
        if (isEverythingValid && isDirty) {
            // Allow to save the customization
            setCanBePublished(false);
            setCanBeSaved(true);
        }
        if (!isEverythingValid) {
            // Prevent to publish or save the customization
            setCanBePublished(false);
            setCanBeSaved(false);
        }
    }, [isEverythingValid, isDirty]);

    // Render functions
    const renderBadge = () => {
        switch (action) {
            case CUSTOMIZATION_URL_ACTIONS.SHOW:
                return (
                    <Badge
                        entity={CUSTOMIZATION_STATUS.PUBLISHED.entity}
                        namespace={nsCustomization}
                        variant={CUSTOMIZATION_STATUS.PUBLISHED.variant}
                    />
                );
            case CUSTOMIZATION_URL_ACTIONS.EDIT:
                return (
                    <Badge
                        entity={CUSTOMIZATION_STATUS.DRAFT.entity}
                        namespace={nsCustomization}
                        variant={CUSTOMIZATION_STATUS.DRAFT.variant}
                    />
                );
            case CUSTOMIZATION_URL_ACTIONS.DUPLICATE:
                return (
                    <Badge
                        entity={CUSTOMIZATION_STATUS.DRAFT.entity}
                        namespace={nsCustomization}
                        variant={CUSTOMIZATION_STATUS.DRAFT.variant}
                    />
                );
            default:
                return (
                    <Badge
                        entity={CUSTOMIZATION_STATUS.NEW.entity}
                        namespace={nsCustomization}
                        variant={CUSTOMIZATION_STATUS.NEW.variant}
                    />
                );
        }
    };

    useEffect(() => {
        setRenderedBadge(renderBadge());
    }, [action]);

    return (
        <div className='flex items-center gap-3 mb-8'>
            {
                isLoading &&
                <Skeleton className={'h-6 w-[300px] p-2 bg-gray-200'} />
            }
            {
                !isLoading &&
                <>
                    {renderedBadge}
                    <Entity entity={customization?.packageLabel ? customization.packageLabel : 'page.payludd.customization.creation.title'} className='text-2xl' />
                    <PageActions actions={
                        !customization?.packageCode || action === CUSTOMIZATION_URL_ACTIONS.DUPLICATE ?
                            [{
                                icon: ICONS.SAVE,
                                entity: 'actions.page.save.title',
                                namespace: nsCustomization,
                                onClick: () => {
                                    if (action === CUSTOMIZATION_URL_ACTIONS.EDIT) {
                                        submitUpdateCustomization.mutate(customizationPackage);
                                    }
                                    else {
                                        submitSaveCustomization.mutate(customizationPackage);
                                    }
                                },
                                visible: true,
                                disabled: !canBeSaved
                            },
                            {
                                icon: ICONS.SEND,
                                entity: 'actions.page.publish.title',
                                namespace: nsCustomization,
                                onClick: () => {
                                    submitPublishCustomization.mutate(customizationPackage.uuid);
                                },
                                visible: true,
                                disabled: !canBePublished || // If the save button is enabled, the publish button is disabled
                                    // If the action is not edit or duplicate, the publish button is disabled
                                    !action ||
                                    // If the action is duplicate and the customization is not modified, the publish button is disabled
                                    (action === CUSTOMIZATION_URL_ACTIONS.DUPLICATE && !isDirty)
                            }] :
                            [{
                                icon: ICONS.CONTENT_COPY,
                                entity: 'actions.page.duplicate',
                                namespace: nsCustomization,
                                onClick: () => {
                                    setBeneficiaries([]);
                                    customization.beneficiaries = [];
                                    const newCustomization = buildUrl(
                                        replaceParameters(
                                            RouteUrls.PAYLUDD_CUSTOMIZATION_MANAGEMENT.url, { contractAccessId: merchantAccountId, customizationId: customization.uuid }
                                        ),
                                        {
                                            view: MERCHANT_ACCOUNT_DETAILS_TABS.CUSTOMIZATION.name,
                                            action: CUSTOMIZATION_URL_ACTIONS.DUPLICATE
                                        }
                                    );
                                    navigate(newCustomization);
                                },
                                visible: true
                            }]
                    }
                        retractable
                        className='!mb-0'
                    />
                    {
                        !isEverythingValid && isDirty &&
                        <TooltipHelper
                            delayDuration={0}
                            trigger={<Icon icon={ICONS.WARNING} className='text-warning' />}
                            content={
                                <div className='flex flex-col gap-2'>
                                    <Entity entity='actions.page.tooltip.error.title' namespace={nsCustomization} />
                                    <ul>
                                        <li className='list-disc list-inside'>
                                            <Entity entity='actions.page.tooltip.error.input_error' namespace={nsCustomization} /></li>
                                        <li className='list-disc list-inside'>
                                            <Entity entity='actions.page.tooltip.error.mode_automate_error' namespace={nsCustomization} />
                                        </li>
                                    </ul>
                                </div>
                            }
                        />
                    }
                </>
            }

        </div>
    );
}

PageActionsHeader.propTypes = {
    customization: PropTypes.object,
    setIsLoading: PropTypes.func,
    action: PropTypes.string,
    isLoading: PropTypes.bool,
    merchantAccountId: PropTypes.string,
    isEverythingValid: PropTypes.bool,
    customizationPackage: PropTypes.object,
    refetchCustomization: PropTypes.func,
    setCustomizationUuid: PropTypes.func,
    isDirty: PropTypes.bool,
    setIsDirty: PropTypes.func,
    setBeneficiaries: PropTypes.func
};
export default PageActionsHeader;